import {Controller} from "@hotwired/stimulus"

// Connects to data-controller="copy-text"
export default class extends Controller {
  static targets = ['copy', 'tick']

  static values = {
    data: String,
    msg: String,
    label: String,
  }

  async copyCode() {
    const that = this
    if (await this.copyToClipboard(this.dataValue)) {
      // Show flash message for event link copy
      if (this.element.classList.contains('event-link-display')) {
        displayFlashMessage('success', this.msgValue)
      }
      
      // Handle visual feedback for copy/tick targets if they exist
      if (this.hasCopyTarget && this.hasTickTarget) {
        this.copyTarget.classList.add('d-none')
        this.tickTarget.classList.remove('d-none')

        if (this.intervalId) {
          clearInterval(this.intervalId)
        }

        this.intervalId = setInterval(function () {
          that.copyTarget.classList.remove('d-none')
          that.tickTarget.classList.add('d-none')
        }, 2000);
      }
    }
  }

  async copyText() {
    if (await this.copyToClipboard(this.dataValue)) {
      displayFlashMessage('success', this.msgValue)
    }
  }

  // Copy + show the design-system copy toast (dark-blue pill, check icon,
  // bottom-center, "<message> — <value>") instead of the generic green flash.
  // Used by the event panel + publish-flow Copy/Share actions.
  async copyToast() {
    if (await this.copyToClipboard(this.dataValue)) {
      this.showCopyToast(this.msgValue, this.hasLabelValue ? this.labelValue : null)
    }
  }

  showCopyToast(message, value) {
    let toast = document.getElementById("copyToast")
    if (!toast) {
      toast = document.createElement("div")
      toast.id = "copyToast"
      toast.className = "copy-toast"
      document.body.appendChild(toast)
    }
    const text = value ? `${message} — ${value}` : message
    toast.innerHTML =
      '<svg class="lucide-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" ' +
      'stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M20 6 9 17l-5-5"/></svg><span></span>'
    toast.querySelector("span").textContent = text

    void toast.offsetWidth // restart the transition if re-triggered
    toast.classList.add("show")
    if (this.toastTimer) clearTimeout(this.toastTimer)
    this.toastTimer = setTimeout(() => toast.classList.remove("show"), 2400)
  }

  openLink(event) {
    const url = event.currentTarget.dataset.url
    if (url) {
      window.open(url, '_blank')
    }
  }

  // Returns a Promise<boolean> that resolves true ONLY when the text actually
  // reached the clipboard, so callers can gate their success toast/flash on a
  // real copy. Modern path is awaited (a rejected writeText no longer reports
  // success); the legacy fallback is required on insecure origins like
  // http://lvh.me where navigator.clipboard is undefined.
  async copyToClipboard(text) {
    // Modern path — only available in secure contexts (https / localhost).
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text)
        return true
      } catch (err) {
        console.error('clipboard.writeText failed, trying fallback:', err)
        // fall through to the execCommand fallback below
      }
    }

    return this.legacyCopy(text)
  }

  // execCommand('copy') fallback for insecure contexts. The textarea MUST live
  // inside this controller's element (which, in the publish/live modals, is
  // inside the focus-trapped .modal subtree) — appended to document.body it sits
  // outside the trap and the selection is dropped, so execCommand copied an
  // empty string while still returning true. select()+setSelectionRange makes
  // the selection explicit, and we verify it stuck before reporting success.
  legacyCopy(text) {
    const host = this.element.closest('.modal') || this.element || document.body
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.setAttribute('readonly', '')
    textArea.style.position = 'fixed'
    textArea.style.top = '0'
    textArea.style.left = '0'
    textArea.style.width = '1px'
    textArea.style.height = '1px'
    textArea.style.padding = '0'
    textArea.style.border = 'none'
    textArea.style.opacity = '0'
    host.appendChild(textArea)

    let ok = false
    try {
      textArea.focus()
      textArea.select()
      textArea.setSelectionRange(0, text.length)
      ok = document.execCommand('copy') &&
           textArea.selectionStart === 0 &&
           textArea.selectionEnd === text.length
    } catch (err) {
      console.error('Failed to copy text:', err)
      ok = false
    } finally {
      textArea.remove()
    }
    return ok
  }
}
